import { type ColumnLike, useVisibleColumns } from '@sazeo/sdk';
import { DataTable } from './DataTable';
import type { DataTableProps } from './DataTable';
import type { ColumnDef } from './types';

function resolveVisibleColumns(columns: ColumnLike[]) {
  try {
    return useVisibleColumns(columns);
  } catch (error) {
    if (
      error instanceof Error &&
      error.message.includes('No "useVisibleColumns" export is defined')
    ) {
      return columns;
    }
    throw error;
  }
}

/**
 * EntityTableView: DataTable with automatic UEAC column visibility filtering.
 *
 * Columns whose `id` maps to visibility='hidden' in RoleUIContext are removed
 * before being passed to DataTable. Use this instead of raw DataTable whenever
 * columns need role-based visibility control.
 */
export type EntityTableViewProps<T> = DataTableProps<T>;

export function EntityTableView<T>(props: EntityTableViewProps<T>) {
  const asCL: ColumnLike[] = (props.columns as ColumnDef<T>[]).map((col) => ({
    ...col,
    id: col.id ?? (col.key as string) ?? '',
  }));

  const visible = resolveVisibleColumns(asCL);
  const visibleIds = new Set(visible.map((c: ColumnLike) => c.id));

  const columns = (props.columns as ColumnDef<T>[]).filter((col) => {
    const id = col.id ?? (col.key as string) ?? '';
    return !id || visibleIds.has(id);
  });

  return <DataTable {...props} columns={columns} />;
}
